// Sensitivity tornado — ±30% sweep on each driver, sorted by swing descending.
// SVG-native. Each row is a bar from low to high operating profit, split at the
// base value (rose left of base = downside; mint right of base = upside).

import { useMemo } from 'react';
import { tornado } from '../../engine/index.js';

const W = 1080;
const ROW_H = 40;
const HEADER_H = 50;
const FOOTER_H = 36;
const LABEL_W = 280;
const PAD_R = 32;
const CHART_X0 = LABEL_W + 24;

function fmtCompact(n) {
  const sign = n < 0 ? '−' : '';
  const a = Math.abs(n);
  if (a >= 1e6) return `${sign}$${(a / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `${sign}$${(a / 1e3).toFixed(1)}k`;
  return `${sign}$${Math.round(a)}`;
}

export default function TornadoChart({ inputs }) {
  const drivers = useMemo(() => {
    if (!inputs) return [];
    try {
      return tornado(inputs);
    } catch {
      return [];
    }
  }, [inputs]);

  if (!drivers.length) {
    return (
      <section className="rounded-card border border-carbon-100 bg-white p-7">
        <p className="caption text-carbon-400 mb-2">Sensitivity tornado</p>
        <p className="body text-carbon-500">No drivers available.</p>
      </section>
    );
  }

  const H = HEADER_H + drivers.length * ROW_H + FOOTER_H;
  const chartW = W - CHART_X0 - PAD_R;

  const allValues = drivers.flatMap((d) => [d.lowY3Cm, d.baseY3Cm, d.highY3Cm]);
  const minVal = Math.min(...allValues, 0);
  const maxVal = Math.max(...allValues, 0);
  const span = maxVal - minVal || 1;
  const pad = span * 0.06;
  const xMin = minVal - pad;
  const xMax = maxVal + pad;
  const xOf = (v) => CHART_X0 + ((v - xMin) / (xMax - xMin)) * chartW;

  const baseValue = drivers[0].baseY3Cm;
  const baseX = xOf(baseValue);

  return (
    <section className="rounded-card border border-carbon-100 bg-white p-7">
      <div className="flex items-baseline justify-between mb-2 flex-wrap gap-3">
        <p className="caption text-carbon-400">Sensitivity tornado</p>
        <p className="label text-carbon-500 num-tabular">±30% sweep · mean scenario</p>
      </div>
      <p className="body text-carbon-500 mb-5">
        Each bar shows the 3-year average operating profit swing when the driver moves ±30% from its
        base value. Drivers sorted by swing magnitude.
      </p>

      <div className="overflow-x-auto nice-scroll">
        <svg
          width="100%"
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="xMidYMid meet"
          className="block"
          style={{ minWidth: 720 }}
          role="img"
          aria-label="Sensitivity tornado: drivers sorted by swing magnitude"
        >
          <line
            x1={baseX}
            y1={HEADER_H - 12}
            x2={baseX}
            y2={HEADER_H + drivers.length * ROW_H + 8}
            stroke="#1F1F1F"
            strokeWidth="1.5"
            strokeDasharray="3 3"
          />
          <text
            x={baseX}
            y={HEADER_H - 18}
            textAnchor="middle"
            fill="#1F1F1F"
            fontSize="11"
            fontFamily="DM Sans, sans-serif"
            fontWeight="500"
          >
            Mean · {fmtCompact(baseValue)}
          </text>

          {drivers.map((d, i) => {
            const yMid = HEADER_H + i * ROW_H + ROW_H / 2;
            const lo = Math.min(d.lowY3Cm, d.highY3Cm);
            const hi = Math.max(d.lowY3Cm, d.highY3Cm);
            const downX0 = xOf(lo);
            const downX1 = Math.min(baseX, xOf(hi));
            const upX0 = Math.max(baseX, xOf(lo));
            const upX1 = xOf(hi);
            const downW = Math.max(0, downX1 - downX0);
            const upW = Math.max(0, upX1 - upX0);

            return (
              <g key={d.label}>
                <text
                  x={CHART_X0 - 12}
                  y={yMid + 4}
                  textAnchor="end"
                  fill="#2E2E2E"
                  fontSize="13"
                  fontFamily="DM Sans, sans-serif"
                >
                  {d.label}
                </text>

                {downW > 0 && (
                  <rect
                    x={downX0}
                    y={yMid - 9}
                    width={downW}
                    height={18}
                    fill="#FB7185"
                    fillOpacity="0.85"
                    rx={3}
                  />
                )}
                {upW > 0 && (
                  <rect
                    x={upX0}
                    y={yMid - 9}
                    width={upW}
                    height={18}
                    fill="#78C24A"
                    fillOpacity="0.85"
                    rx={3}
                  />
                )}

                <text
                  x={downX0 - 6}
                  y={yMid + 4}
                  textAnchor="end"
                  fill="#525252"
                  fontSize="11"
                  fontFamily="DM Sans, sans-serif"
                >
                  {fmtCompact(lo)}
                </text>
                <text
                  x={upX1 + 6}
                  y={yMid + 4}
                  textAnchor="start"
                  fill="#525252"
                  fontSize="11"
                  fontFamily="DM Sans, sans-serif"
                >
                  {fmtCompact(hi)}
                </text>
              </g>
            );
          })}

          <g>
            <line
              x1={CHART_X0}
              y1={HEADER_H + drivers.length * ROW_H + 14}
              x2={W - PAD_R}
              y2={HEADER_H + drivers.length * ROW_H + 14}
              stroke="#D4D4D4"
              strokeWidth="1"
            />
            <text
              x={CHART_X0}
              y={HEADER_H + drivers.length * ROW_H + 30}
              fill="#757575"
              fontSize="11"
              fontFamily="DM Sans, sans-serif"
            >
              {fmtCompact(xMin)}
            </text>
            <text
              x={W - PAD_R}
              y={HEADER_H + drivers.length * ROW_H + 30}
              textAnchor="end"
              fill="#757575"
              fontSize="11"
              fontFamily="DM Sans, sans-serif"
            >
              {fmtCompact(xMax)}
            </text>
          </g>
        </svg>
      </div>

      <Legend />
    </section>
  );
}

function Legend() {
  return (
    <div className="flex items-center gap-5 mt-4 flex-wrap">
      <LegendItem color="#FB7185" label="Downside (driver moves output below base)" />
      <LegendItem color="#78C24A" label="Upside (driver moves output above base)" />
    </div>
  );
}

function LegendItem({ color, label }) {
  return (
    <div className="flex items-center gap-2">
      <span
        className="h-2.5 w-3.5 rounded-sw shrink-0"
        style={{ background: color }}
        aria-hidden="true"
      />
      <span className="label text-carbon-500">{label}</span>
    </div>
  );
}
